import { signal, computed } from '@preact/signals';
import { dataHub } from '../lib/datahub';
import { RightRail } from '../shell/RightRail';
import { svgDonut, svgSparkline } from '../lib/sparkline';
import { resolvedOutcomeRows, ledgerSummary } from '../lib/ledgerStats';
import { RESEARCH_PAPER_DEFAULT } from '../lib/copy';

// Home / landing — Preact port of renderHome + wireLiveStatus + wireClock.
// Live status, market clock, real outcome table + accountability charts from
// the typed ledger. Research and paper by default; owner-only live execution is off unless armed.

const MODULES: Array<[string, string, string, string, string]> = [
  ['Options', '/options', 'Price, volatility, and options analysis across chains.', 'om-good', 'UP'],
  ['Day Trade', '/daytrade', 'Intraday setups, momentum, and risk modeling.', 'om-cyan', 'II'],
  ['Commodities', '/commodities', 'Futures, metals, energy, and softs analysis.', 'om-gold', '|||'],
  ['Futures', '/futures', 'Contract specs, strategy atlas, and hedge education. No orders.', 'om-cyan', 'FU'],
  ['Forex', '/forex', 'Currency analysis, correlations, and carry insights.', 'om-cyan', 'FX'],
  ['Crypto', '/crypto', 'Spot, derivatives, and market intelligence.', 'om-purple', 'B'],
  ['Event Signals', '/dashboard', 'Earnings, economic events, and catalysts.', 'om-warn', 'EV'],
  ['Pattern Engine', '/engine', 'Historical pattern research, gated signals, and paper practice.', 'om-gold', 'PE'],
];

const dhPill = signal<{ text: string; cls: string }>({ text: 'Checking...', cls: 'om-muted' });
const dhNote = signal<{ text: string; cls: string }>({ text: 'Verifying Oculus-Clipeus session...', cls: 'om-muted' });
const dhMode = signal<{ text: string; cls: string }>({ text: '-', cls: 'om-muted' });
const dhFeed = signal<{ text: string; cls: string }>({ text: '-', cls: 'om-muted' });
const dhSync = signal<string>('-');
const dhTz = signal<string>('-');
const nowTick = signal<number>(Date.now());
const clockData = signal<{ isOpen?: boolean; nextOpen?: string; nextClose?: string; source?: string } | null>(null);

const etNow = () => {
  void nowTick.value; // re-evaluate each tick
  try {
    return new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch {
    return new Date().toLocaleTimeString();
  }
};
const etAt = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};
const clock = computed(() => {
  const t = etNow();
  const c = clockData.value;
  if (!c || c.source === 'unavailable' || typeof c.isOpen !== 'boolean') return { head: 'Market clock', sub: `ET ${t}`, tone: 'om-muted' };
  if (c.isOpen) return { head: 'Market Open', sub: `ET ${t}${c.nextClose ? ' - closes ' + etAt(c.nextClose) : ''}`, tone: 'om-good' };
  return { head: 'Market Closed', sub: `ET ${t}${c.nextOpen ? ' - opens ' + etAt(c.nextOpen) : ''}`, tone: 'om-bad' };
});

let started = false;
function initStatus(): void {
  if (started) return;
  started = true;
  dhSync.value = new Date().toLocaleTimeString();
  try {
    dhTz.value = Intl.DateTimeFormat().resolvedOptions().timeZone || '-';
  } catch {
    /* keep '-' */
  }
  dataHub
    .health()
    .then((h) => {
      const ok = !!(h && (h.ok || h.service));
      dhPill.value = ok ? { text: 'Online', cls: 'om-good' } : { text: 'Offline', cls: 'om-bad' };
      dhNote.value = ok ? { text: 'Reachable' + (h.provider ? ' - ' + h.provider : ''), cls: 'om-good' } : { text: 'DataHub unreachable.', cls: 'om-bad' };
      dhFeed.value = ok ? { text: 'Reachable', cls: 'om-good' } : { text: '-', cls: 'om-muted' };
    })
    .catch(() => {
      dhPill.value = { text: 'Offline', cls: 'om-bad' };
      dhNote.value = { text: 'DataHub unreachable.', cls: 'om-bad' };
    });
  dataHub
    .authStatus()
    .then((s) => {
      dhMode.value = s && s.authenticated ? { text: 'Signed in' + (s.role ? ' - ' + s.role : ''), cls: 'om-good' } : { text: 'Locked', cls: 'om-muted' };
    })
    .catch(() => {
      dhMode.value = { text: '-', cls: 'om-muted' };
    });
  const fetchClock = () =>
    dataHub
      .marketClock()
      .then((c) => (clockData.value = c))
      .catch(() => (clockData.value = { source: 'unavailable' }));
  fetchClock();
  setInterval(fetchClock, 60000);
  setInterval(() => (nowTick.value = Date.now()), 1000);
}

export function Home() {
  initStatus();
  const c = clock.value;
  const rows = resolvedOutcomeRows(8);
  const s = ledgerSummary();
  const hitRate = s.resolved ? Math.round((s.hits / s.resolved) * 100) : null;
  const donut = svgDonut(
    [
      { value: s.hits, cls: 'good' },
      { value: s.miss, cls: 'bad' },
      { value: s.open + s.fcOpen, cls: 'warn' },
    ],
    hitRate == null ? '-' : `${hitRate}%`,
    'No resolved paper forecasts yet. Record outcomes on logged setups to build your accuracy.',
  );
  const curve =
    s.series.length > 1
      ? svgSparkline(s.series, { empty: '' })
      : '<div class="om-muted" style="padding:10px 2px">Cumulative result appears once you resolve forecasts.</div>';

  return (
    <section class="om-home-grid">
      <div class="om-main-stack">
        <section class="om-home-top">
          <div class="om-home-title">
            <h1>Trader Suite</h1>
            <p>Data gathering and analysis workstation.</p>
            <p class="om-good">{RESEARCH_PAPER_DEFAULT}</p>
          </div>
          <div class="om-home-status om-frame">
            <div>
              <strong class="om-good"><span class={`om-dh-pill ${dhPill.value.cls}`}>{dhPill.value.text}</span></strong>
              <p class={`om-dh-note ${dhNote.value.cls}`}>{dhNote.value.text}</p>
            </div>
            <div><span>Session</span><strong class={`om-dh-mode ${dhMode.value.cls}`}>{dhMode.value.text}</strong></div>
            <div><span>Feeds</span><strong class={`om-dh-feed ${dhFeed.value.cls}`}>{dhFeed.value.text}</strong></div>
            <div class="om-clock"><strong class={c.tone}>{c.head}</strong> <span class="om-muted">{c.sub}</span></div>
            <div><span>Last Checked</span><strong class="om-dh-sync">{dhSync.value}</strong><small class="om-dh-tz om-muted">{dhTz.value}</small></div>
            <a class="om-btn" href="/dashboard">System Status</a>
          </div>
        </section>

        <section class="om-card om-glass">
          <div class="om-card-head">
            <div><h2>Launch Calculators</h2><p class="om-card-sub">Open any module to run analysis, build scenarios, and save results.</p></div>
          </div>
          <div class="om-launch-grid">
            {MODULES.map((m) => (
              <a class="om-module-tile om-glass" href={m[1]}>
                <span class={`om-module-symbol ${m[3]}`}>{m[4]}</span>
                <span class="om-module-copy"><strong class={m[3]}>{m[0]}</strong><em>{m[2]}</em></span>
                <span class="om-module-open">Open</span>
              </a>
            ))}
          </div>
        </section>

        <section class="om-card om-glass">
          <div class="om-card-head"><h2>Projected vs Outcome</h2><a class="om-btn" href="/portfolio">View All Forecasts</a></div>
          {rows.length === 0 ? (
            <div class="om-muted" style="padding:10px 2px">No resolved paper trades yet. Log a setup from any calculator and record its outcome to populate this.</div>
          ) : (
            <div class="om-table-scroll">
              <table>
                <thead><tr><th>Symbol</th><th>What</th><th>Account</th><th>Result</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr><td>{r.symbol}</td><td>{r.what}</td><td>{r.acct}</td><td><span class={`om-pill ${r.hit ? '' : 'red'}`}>{r.hit ? 'Hit' : 'Miss'}</span></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section class="om-card om-glass">
          <div class="om-card-head"><div><h2>Forecast Accountability</h2><span class="om-card-sub">Projected vs outcome from your paper ledger - hit rate and cumulative result. Paper only.</span></div></div>
          <div class="om-charts-grid">
            <div class="om-chart-cell">
              <div class="om-chart-title">Hit rate</div>
              <div dangerouslySetInnerHTML={{ __html: donut }} />
              <div class="om-chart-legend"><span><i class="om-dot good"></i>Hit {s.hits}</span><span><i class="om-dot bad"></i>Miss {s.miss}</span><span><i class="om-dot warn"></i>Open {s.open + s.fcOpen}</span></div>
            </div>
            <div class="om-chart-cell">
              <div class="om-chart-title">Cumulative result (hits - misses)</div>
              <div dangerouslySetInnerHTML={{ __html: curve }} />
            </div>
          </div>
        </section>

        <section class="om-footer-banner om-frame">
          <span>Open Forecasts <strong class="om-ledger-open">{s.open}</strong></span>
          <span>Hit Rate <strong class="om-ledger-hit">{s.resolved ? `${Math.round((s.hits / s.resolved) * 100)}%` : '-'}</strong></span>
          <span>Data Integrity <strong class="om-ledger-integrity">{s.total ? `${Math.round((s.valid / s.total) * 100)}%` : '-'}</strong></span>
          <span>Paper Only</span>
        </section>
      </div>
      <RightRail kind="home" />
    </section>
  );
}
